import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../../store';
import axios from '../../../axios';
import { AnnotationStandardDetails, FindingTypeDetails } from '../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import { PropConstants, defectLocations, notItemsColumns } from './Helpers';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';

export type IdText = {
  id: string | number;
  text?: string;
};

export type ResponseLevel = {
  id: string;
  code: string;
  name: string;
  description: string;
};

export type MaintenanceTrigger = {
  id: string;
  name: string;
  version: string;
  default: boolean;
  annotationStandardId: string;
  items: MaintenanceTriggerItem[];
};

type MaintenanceTriggerItem = {
  id: string;
  findingTypeId: string;
  defectLocation: string;
  comment: string;
  responseLevelId: number;
  responseLevelCode: string;
  responseLevelDescription: string;
  thresholdItems: MaintenanceTriggerItemThresholdItem[];
  findingTypePropertyItems: MaintenanceTriggerItemFindingTypeProperty[];
};

type MaintenanceTriggerItemThresholdItem = {
  id: string;
  name: string;
  value: string;
};

type MaintenanceTriggerItemFindingTypeProperty = {
  id: string;
  value: string;
  findingTypePropertyId: string;
};

type CreateMaintenanceTrigger = {
  name: string;
  version: string;
  default: boolean;
  annotationStandardId: string;
  items: CreateMaintenanceTriggerItem[];
};

type CreateMaintenanceTriggerItem = {
  findingTypeId: string;
  defectLocation: string;
  comment: string;
  responseLevelId: number;
  thresholdItems: CreateMaintenanceTriggerItemThresholdItem[];
  findingTypePropertyItems: CreateMaintenanceTriggerItemFindingTypeProperty[];
};

type CreateMaintenanceTriggerItemThresholdItem = {
  name: string;
  value: string;
};

type CreateMaintenanceTriggerItemFindingTypeProperty = {
  value: string;
  findingTypePropertyId: string;
};

type UpdateMaintenanceTrigger = {
  id: string;
  name: string;
  version: string;
  default: boolean;
  items: UpdateMaintenanceTriggerItem[];
};

type UpdateMaintenanceTriggerItem = {
  id: string;
  findingTypeId: string;
  defectLocation: string;
  comment: string;
  responseLevelId: number;
  thresholdItems: UpdateMaintenanceTriggerItemThresholdItem[];
  findingTypePropertyItems: UpdateMaintenanceTriggerItemFindingTypeProperty[];
};

type UpdateMaintenanceTriggerItemThresholdItem = {
  id?: string;
  name: string;
  value: string;
};

type UpdateMaintenanceTriggerItemFindingTypeProperty = {
  id?: string;
  value: string;
  findingTypePropertyId: string;
};

type State = {
  responseLevels: ResponseLevel[];
  standards: IdText[];
  standard: AnnotationStandardDetails | null;
  maintenanceTrigger: MaintenanceTrigger | null;
  standardHasDefaultTrigger: boolean;
  isCloning: boolean;
  clonedUrl: string;
  isLoading: boolean;
  finished: boolean;
};

const initialState: State = {
  responseLevels: [],
  standards: [],
  standard: null,
  maintenanceTrigger: null,
  standardHasDefaultTrigger: false,
  isCloning: false,
  clonedUrl: '',
  isLoading: false,
  finished: false
};

const maintenanceTriggerFormSlice = createSlice({
  name: 'maintenanceTriggerForm',
  initialState,
  reducers: {
    setStandards(state, action: PayloadAction<IdText[]>) {
      state.standards = action.payload;
    },
    setStandard(state, action: PayloadAction<AnnotationStandardDetails>) {
      state.standard = action.payload;
      state.standardHasDefaultTrigger = action.payload.hasDefaultMaintenanceTrigger;
    },
    setResponseLevels(state, action: PayloadAction<ResponseLevel[]>) {
      state.responseLevels = action.payload;
    },
    setMaintenanceTrigger(state, action: PayloadAction<MaintenanceTrigger>) {
      state.maintenanceTrigger = action.payload;
    },
    setIsCloning(state, action: PayloadAction<boolean>) {
      state.isCloning = action.payload;
    },
    setClonedUrl(state, action: PayloadAction<string>) {
      state.clonedUrl = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    resetState(state) {
      return initialState;
    },
    setFinished(state, action: PayloadAction<boolean>) {
      state.finished = action.payload;
    }
  }
});

export const {
  setStandards,
  setStandard,
  setResponseLevels,
  setMaintenanceTrigger,
  setIsCloning,
  setClonedUrl,
  setIsLoading,
  resetState,
  setFinished
} = maintenanceTriggerFormSlice.actions;

export default maintenanceTriggerFormSlice.reducer;

export const getResponseLevels = (): AppThunk => async dispatch => {
  const response = await axios.get<Array<ResponseLevel>>('MaintenanceTrigger/response-levels');
  dispatch(setResponseLevels(response.data));
};

export const getStandards = (): AppThunk => async dispatch => {
  const response = await axios.get<Array<IdText>>('annotationstandards/listofnames');
  dispatch(setStandards(response.data));
};

export const getStandardDetails =
  (id: string): AppThunk =>
  async dispatch => {
    const url = `AnnotationStandards/${id}`;
    const response = await axios.get<AnnotationStandardDetails>(url);
    dispatch(setStandard(response.data));
  };

export const getMaintenanceTrigger =
  (id: string): AppThunk =>
  async dispatch => {
    const url = `MaintenanceTrigger/${id}`;
    try {
      const response = await axios.get<MaintenanceTrigger>(url);
      dispatch(setMaintenanceTrigger(response.data));
    } catch (error) {
      console.log(error);
    }
  };

export const getMaintenanceTriggers =
  (setMaintenanceTriggers: Function): AppThunk =>
  async dispatch => {
    const url = `MaintenanceTrigger/all`;
    try {
      const response = await axios.get<MaintenanceTrigger>(url);
      dispatch(setMaintenanceTriggers(response.data));
    } catch (error) {
      console.log(error);
    }
  };

export const createMaintenanceTrigger =
  (name: string, version: string, defaultCheckbox: boolean, rules: any[]): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const url = 'MaintenanceTrigger';
    const { standard } = getState().maintenanceTriggerForm;
    const findingTypes = standard!.findingTypes;
    const annotationStandardId = standard!.id!;

    const items = prepareItemsForCreate(rules, findingTypes);

    const model: CreateMaintenanceTrigger = {
      name,
      version,
      default: defaultCheckbox,
      annotationStandardId,
      items
    };

    try {
      const response = await axios.post(url, model);

      dispatch(setFinished(true));

      ToastAnt('Added');
    } catch (error) {
      console.log(error);
      ToastAnt('An error occured...');
    }
    dispatch(setIsLoading(false));
  };

export const updateMaintenanceTrigger =
  (name: string, version: string, defaultCheckbox: boolean, rules: any[]): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const url = 'MaintenanceTrigger';
    const findingTypes = getState().maintenanceTriggerForm.standard!.findingTypes;
    const dbModel = getState().maintenanceTriggerForm.maintenanceTrigger!;

    const items = rules.map(rule => {
      const findingTypePropertyItems: UpdateMaintenanceTriggerItemFindingTypeProperty[] = [];
      const thresholdItems: UpdateMaintenanceTriggerItemThresholdItem[] = [];
      const properties = findingTypes.find(x => x.id === rule.findingType)!.findingTypeTemplate!.properties;
      const dbRule = dbModel.items.find(x => x.id === rule[PropConstants.ID]);

      const bladeZone = properties.find(x => x.name === PropConstants.BLADE_ZONE);
      if (bladeZone) {
        const enumValue = defectLocations.find(x => x.id === +rule[PropConstants.DEFECT_LOCATION]);
        findingTypePropertyItems.push({
          findingTypePropertyId: bladeZone.id,
          value: enumValue?.text ?? ''
        });
      }

      for (const prop in rule) {
        if (notItemsColumns.includes(prop)) continue;

        if (Object.prototype.hasOwnProperty.call(rule, prop)) {
          const value = rule[prop];
          if (!value.trim() || prop === PropConstants.ID || prop === PropConstants.INDEX) continue;

          const findingTypeProperty = properties.find(x => x.name === prop);
          if (findingTypeProperty) {
            const itemToAdd: UpdateMaintenanceTriggerItemFindingTypeProperty = {
              id: undefined,
              findingTypePropertyId: findingTypeProperty.id,
              value
            };

            if (dbRule) {
              const dbProperty = dbRule.findingTypePropertyItems.find(x => x.findingTypePropertyId === findingTypeProperty.id);
              if (dbProperty) {
                itemToAdd.id = dbProperty.id;
              }
            }

            findingTypePropertyItems.push(itemToAdd);
          } else {
            const itemToAdd: UpdateMaintenanceTriggerItemThresholdItem = {
              id: undefined,
              name: prop,
              value
            };

            if (dbRule) {
              const dbProperty = dbRule.thresholdItems.find(x => x.name === prop);
              if (dbProperty) {
                itemToAdd.id = dbProperty.id;
              }
            }

            thresholdItems.push(itemToAdd);
          }
        }
      }

      const defectLocation = setDefectLocation(rule);

      const item: UpdateMaintenanceTriggerItem = {
        id: rule[PropConstants.ID],
        findingTypeId: rule[PropConstants.FINDING_TYPE],
        defectLocation: defectLocation,
        responseLevelId: rule[PropConstants.RESPONSE_LEVEL_ACTION],
        comment: rule.comments,
        findingTypePropertyItems,
        thresholdItems
      };

      return item;
    });

    const model: UpdateMaintenanceTrigger = {
      id: dbModel.id,
      name,
      version,
      default: defaultCheckbox,
      items
    };

    try {
      const response = await axios.put(url, model);

      dispatch(setFinished(true));

      ToastAnt('Updated');
    } catch (error) {
      console.log(error);
      ToastAnt('An error occured...');
    }
    dispatch(setIsLoading(false));
  };

export const cloneMaintenanceTrigger =
  (name: string, rules: any[]): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setIsCloning(true));

    const url = 'MaintenanceTrigger';
    const { standard } = getState().maintenanceTriggerForm;
    const findingTypes = standard!.findingTypes;
    const annotationStandardId = standard!.id!;

    const items = prepareItemsForCreate(rules, findingTypes);

    const model: CreateMaintenanceTrigger = {
      name,
      version: '',
      default: false,
      annotationStandardId,
      items
    };

    try {
      const response = await axios.post<string>(url, model);
      dispatch(setClonedUrl(`/maintenance-triggers/${response.data}`));
    } catch (error) {
      console.log(error);
    }

    dispatch(setIsCloning(false));
  };

const prepareItemsForCreate = (rules: any[], findingTypes: FindingTypeDetails[]) => {
  return rules.map(rule => {
    const findingTypePropertyItems: CreateMaintenanceTriggerItemFindingTypeProperty[] = [];
    const thresholdItems: CreateMaintenanceTriggerItemThresholdItem[] = [];
    const properties = findingTypes.find(x => x.id === rule.findingType)!.findingTypeTemplate!.properties;

    const bladeZone = properties.find(x => x.name === PropConstants.BLADE_ZONE);
    if (bladeZone) {
      const enumValue = defectLocations.find(x => x.id === +rule[PropConstants.DEFECT_LOCATION]);
      findingTypePropertyItems.push({
        findingTypePropertyId: bladeZone.id,
        value: enumValue?.text ?? ''
      });
    }

    for (const prop in rule) {
      if (notItemsColumns.includes(prop)) continue;

      if (Object.prototype.hasOwnProperty.call(rule, prop)) {
        const value = rule[prop];
        if (!value.trim() || prop === PropConstants.ID || prop === PropConstants.INDEX) continue;

        const findingTypeProperty = properties.find(x => x.name === prop);
        if (findingTypeProperty) {
          findingTypePropertyItems.push({
            findingTypePropertyId: findingTypeProperty.id,
            value
          });
        } else {
          thresholdItems.push({
            name: prop,
            value
          });
        }
      }
    }

    const defectLocation = setDefectLocation(rule);

    const item: CreateMaintenanceTriggerItem = {
      findingTypeId: rule[PropConstants.FINDING_TYPE],
      defectLocation: defectLocation,
      responseLevelId: rule[PropConstants.RESPONSE_LEVEL_ACTION],
      comment: rule.comments,
      findingTypePropertyItems,
      thresholdItems
    };

    return item;
  });
};

const setDefectLocation = (rule: any) => {
  let defectLocation = rule[PropConstants.DEFECT_LOCATION];

  if (defectLocation === ' ' || defectLocation === '' || defectLocation === null) {
    defectLocation = null;
  }

  return defectLocation;
};
